import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Search } from "react-feather";
import "./searchable-popover.scss";

export interface SearchablePopoverOption {
  label: string;
  value: string;
}

export interface SearchablePopoverProps {
  options: SearchablePopoverOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, option: SearchablePopoverOption) => void;
  searchPlaceholder?: string;
  heading?: string;
  onSearchChange?: (searchTerm: string) => void;
  /** Callback fired when the user clicks the "Create" option. Receives the current search term. */
  onCreateOption?: (value: string) => void;
  /** When true the popover content renders via a React portal into document.body */
  shouldUsePortal?: boolean;
  /** Anchor element ref used to position the portal overlay */
  anchorRef?: React.RefObject<HTMLElement>;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export const SearchablePopover: React.FC<SearchablePopoverProps> = ({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  searchPlaceholder = "Search for an option...",
  heading = "Select an option or create one",
  onSearchChange,
  onCreateOption,
  shouldUsePortal = false,
  anchorRef,
  disabled = false,
  className = "",
  id,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const [searchTerm, setSearchTerm] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  const activeValue = isControlled ? controlledValue : internalValue;

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const lower = searchTerm.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    onSearchChange?.(val);
  };

  const handleSelect = (option: SearchablePopoverOption) => {
    if (disabled) return;
    if (!isControlled) {
      setInternalValue(option.value);
    }
    onChange?.(option.value, option);
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent,
    option: SearchablePopoverOption,
    index: number
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(option);
      return;
    }

    const items = menuRef.current?.querySelectorAll<HTMLDivElement>(
      ".searchable-popover__option"
    );
    if (!items) return;

    let nextIndex = -1;
    if (e.key === "ArrowDown") {
      nextIndex = (index + 1) % items.length;
    } else if (e.key === "ArrowUp") {
      nextIndex = (index - 1 + items.length) % items.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = items.length - 1;
    }

    if (nextIndex >= 0) {
      e.preventDefault();
      items[nextIndex]?.focus();
    }
  };

  const handleCreateClick = () => {
    if (disabled || !searchTerm.trim()) return;
    onCreateOption?.(searchTerm.trim());
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCreateClick();
    }
  };

  const showCreateRow =
    filteredOptions.length === 0 && searchTerm.trim().length > 0;

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const firstItem = menuRef.current?.querySelector<HTMLDivElement>(
        ".searchable-popover__option, .searchable-popover__create-option"
      );
      firstItem?.focus();
    }
  };

  const updatePortalPosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor || !shouldUsePortal) return;

    const rect = anchor.getBoundingClientRect();
    const gap = 4;

    setPortalStyle({
      position: "fixed",
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  }, [shouldUsePortal, anchorRef]);

  useEffect(() => {
    if (!shouldUsePortal) return;
    updatePortalPosition();
    window.addEventListener("scroll", updatePortalPosition, true);
    window.addEventListener("resize", updatePortalPosition);
    return () => {
      window.removeEventListener("scroll", updatePortalPosition, true);
      window.removeEventListener("resize", updatePortalPosition);
    };
  }, [shouldUsePortal, updatePortalPosition]);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const content = (
    <div
      ref={containerRef}
      className={`searchable-popover ${disabled ? "searchable-popover--disabled" : ""} ${className}`}
      style={shouldUsePortal ? portalStyle : undefined}
      id={id}
      role="dialog"
      aria-label={heading}
    >
      <div className="searchable-popover__search">
        <div className="searchable-popover__search-field">
          <input
            ref={searchInputRef}
            type="text"
            className="searchable-popover__search-input"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            aria-label={searchPlaceholder}
            disabled={disabled}
          />
          <span className="searchable-popover__search-icon" aria-hidden="true">
            <Search size={14} />
          </span>
        </div>
      </div>

      <div className="searchable-popover__divider" />

      <p className="searchable-popover__heading">{heading}</p>

      <div className="searchable-popover__menu" ref={menuRef} role="listbox">
        <div className="searchable-popover__menu-items">
          {filteredOptions.length === 0 && !showCreateRow && (
            <div className="searchable-popover__empty">No options found</div>
          )}
          {showCreateRow && (
            <div
              className="searchable-popover__create-option"
              role="option"
              aria-selected={false}
              tabIndex={0}
              onClick={handleCreateClick}
              onKeyDown={handleCreateKeyDown}
            >
              <button
                type="button"
                className="searchable-popover__create-label"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateClick();
                }}
                tabIndex={-1}
              >
                Create
              </button>
              <div className="searchable-popover__create-content">
                <span className="searchable-popover__create-value">
                  {searchTerm.trim()}
                </span>
              </div>
            </div>
          )}
          {filteredOptions.map((option, index) => (
            <div
              key={option.value}
              className={`searchable-popover__option ${option.value === activeValue ? "searchable-popover__option--selected" : ""}`}
              role="option"
              aria-selected={option.value === activeValue}
              tabIndex={0}
              onClick={() => handleSelect(option)}
              onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
            >
              <div className="searchable-popover__option-content">
                <span className="searchable-popover__option-label">
                  {option.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (shouldUsePortal) {
    return createPortal(content, document.body);
  }

  return content;
};
